import { Field } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/tailwindui/fieldset';
import { Field as FieldComponent } from '@/src/tailwindui/fieldset';
import ImageInput from '@/src/Processing/components/ImageInput';
import Library from '@/src/Processing/Library/components/Library';
import { useState } from 'react';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import { ColorPicker } from '@/src/ExperienceBuilder/ColorPicker';
import GalleryTagFilterSettings from './GalleryTagFilterSettings';
import { ClassicAppearanceNotice } from '../../GalleryExperienceEditor/ExperienceEditorLink';

interface WebGallerySettingsProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    hasPictimeSettings: boolean;
}

export default function WebGallerySettings({
    values,
    setFieldValue,
    hasPictimeSettings,
}: WebGallerySettingsProps) {
    const [showLibrary, setShowLibrary] = useState(false);
    const [libraryCallback, setLibraryCallback] = useState<
        ((value: string) => void) | null
    >(null);

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Web Gallery Settings
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure the visual appearance and behavior of the web
                    gallery.
                </p>
            </div>

            {/* Basic Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Basic Settings
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Configure the basic gallery information and access
                        settings.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldComponent>
                        <Label htmlFor="name">Gallery Name</Label>
                        <Field
                            as={Input}
                            id="name"
                            name="name"
                            placeholder="Enter gallery name"
                        />
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="slug">Gallery Slug</Label>
                        <Field
                            as={Input}
                            id="slug"
                            name="slug"
                            placeholder="gallery-slug"
                        />
                    </FieldComponent>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="isPrivate"
                            name="isPrivate"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="isPrivate"
                            className="text-sm font-medium"
                        >
                            Password protected
                        </Label>
                    </div>
                </FieldComponent>

                {values.isPrivate && (
                    <FieldComponent>
                        <Label htmlFor="password">Password</Label>
                        <Field
                            as={Input}
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Enter password"
                            autocomplete="off"
                        />
                    </FieldComponent>
                )}

                <FieldComponent>
                    <Label htmlFor="galleryForwardUrl">
                        Gallery forwarding URL
                    </Label>
                    <Field
                        as={Input}
                        id="galleryForwardUrl"
                        name="galleryForwardUrl"
                        type="url"
                        placeholder="https://your-event-gallery.com/"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Forward visits to the gallery link itself to a custom,
                        externally hosted gallery. We append activationId and
                        galleryId as query parameters. Share links use the
                        separate forwarding URL under Sharing settings. Leave
                        blank to use the built-in gallery.
                    </p>
                </FieldComponent>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

            {/* Style & Branding */}
            <ClassicAppearanceNotice
                enabled={values.galleryExperience?.enabled}
            />
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Style & Branding
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Customize the visual appearance and branding of your
                        gallery.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldComponent>
                        <Label htmlFor="backgroundColor">
                            Background Color
                        </Label>
                        <div className="flex items-center gap-2">
                            <ColorPicker
                                value={values.backgroundColor || '#FFFFFF'}
                                onChange={(value) =>
                                    setFieldValue('backgroundColor', value)
                                }
                            />
                            <Field
                                as={Input}
                                id="backgroundColor"
                                name="backgroundColor"
                                placeholder="#FFFFFF"
                                className="flex-1"
                            />
                        </div>
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="foregroundColor">
                            Foreground Color
                        </Label>
                        <div className="flex items-center gap-2">
                            <ColorPicker
                                value={values.foregroundColor || '#000000'}
                                onChange={(value) =>
                                    setFieldValue('foregroundColor', value)
                                }
                            />
                            <Field
                                as={Input}
                                id="foregroundColor"
                                name="foregroundColor"
                                placeholder="#000000"
                                className="flex-1"
                            />
                        </div>
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="headerBackgroundColor">
                            Header Background Color
                        </Label>
                        <div className="flex items-center gap-2">
                            <ColorPicker
                                value={
                                    values.headerBackgroundColor || '#000000'
                                }
                                onChange={(value) =>
                                    setFieldValue(
                                        'headerBackgroundColor',
                                        value,
                                    )
                                }
                            />
                            <Field
                                as={Input}
                                id="headerBackgroundColor"
                                name="headerBackgroundColor"
                                placeholder="#000000"
                                className="flex-1"
                            />
                        </div>
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="headerForegroundColor">
                            Header Foreground Color
                        </Label>
                        <div className="flex items-center gap-2">
                            <ColorPicker
                                value={
                                    values.headerForegroundColor || '#FFFFFF'
                                }
                                onChange={(value) =>
                                    setFieldValue(
                                        'headerForegroundColor',
                                        value,
                                    )
                                }
                            />
                            <Field
                                as={Input}
                                id="headerForegroundColor"
                                name="headerForegroundColor"
                                placeholder="#FFFFFF"
                                className="flex-1"
                            />
                        </div>
                    </FieldComponent>
                </div>

                <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                        Gallery Logo
                    </h5>
                    <div className="mt-2 flex items-center space-x-4 flex-col">
                        <div className="flex items-center space-x-2 w-full mb-2">
                            <Field
                                as={Input}
                                id="logo"
                                name="logo"
                                value={values.logo}
                                placeholder="https://example.com/image.png"
                            />
                        </div>
                        <div className="flex items-center space-x-2 w-full">
                            <ImageInput
                                image={values.logo}
                                onImageChange={(image) =>
                                    setFieldValue('logo', image)
                                }
                                key={values.logo}
                            />
                            <FeatureFlag flag={FeatureFlags.PE_LIBRARY}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        const callback = (string: string) => {
                                            console.log('LOGO', string);
                                            setFieldValue('logo', string);
                                        };
                                        setLibraryCallback(() => callback);
                                        setShowLibrary(true);
                                    }}
                                >
                                    Choose from Library
                                </Button>
                            </FeatureFlag>
                        </div>
                    </div>
                </div>

                <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                        Background Image
                    </h5>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Optional. Fills the whole page behind the header and
                        gallery and replaces the background colors while set.
                        Use a landscape image at least 1920×1080, JPG or WebP
                        under 1 MB, with key detail centered — edges crop on
                        phones.
                    </p>
                    <div className="mt-2 flex items-center space-x-4 flex-col">
                        <div className="flex items-center space-x-2 w-full mb-2">
                            <Field
                                as={Input}
                                id="backgroundImage"
                                name="backgroundImage"
                                value={values.backgroundImage || ''}
                                placeholder="https://example.com/background.jpg"
                            />
                        </div>
                        <div className="flex items-center space-x-2 w-full">
                            <ImageInput
                                image={values.backgroundImage}
                                onImageChange={(image) =>
                                    setFieldValue('backgroundImage', image)
                                }
                                key={values.backgroundImage}
                            />
                            <FeatureFlag flag={FeatureFlags.PE_LIBRARY}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        const callback = (string: string) => {
                                            setFieldValue(
                                                'backgroundImage',
                                                string,
                                            );
                                        };
                                        setLibraryCallback(() => callback);
                                        setShowLibrary(true);
                                    }}
                                >
                                    Choose from Library
                                </Button>
                            </FeatureFlag>
                            {values.backgroundImage && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        setFieldValue('backgroundImage', null)
                                    }
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

            {/* Call-to-Action Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Call-to-Action Settings
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Configure the call-to-action button and link settings.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldComponent>
                        <Label htmlFor="ctaText">Text</Label>
                        <Field
                            as={Input}
                            id="ctaText"
                            name="ctaText"
                            placeholder="Click here to learn more"
                        />
                    </FieldComponent>

                    <FieldComponent>
                        <Label htmlFor="ctaLink">Link</Label>
                        <Field
                            as={Input}
                            id="ctaLink"
                            name="ctaLink"
                            placeholder="https://example.com"
                        />
                    </FieldComponent>
                </div>

                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Image
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Upload a wide banner, ideally 1290×430 (3:1), with
                        important content centered.
                    </p>
                    <div className="mt-2 flex items-center space-x-4 flex-col">
                        <div className="flex items-center space-x-2 w-full mb-2">
                            <Field
                                as={Input}
                                id="ctaImage"
                                name="ctaImage"
                                value={values.ctaImage}
                                placeholder="https://example.com/image.png"
                            />
                        </div>
                        <div className="flex items-center space-x-2 w-full">
                            <ImageInput
                                image={values.ctaImage}
                                onImageChange={(image) =>
                                    setFieldValue('ctaImage', image)
                                }
                                key={values.ctaImage}
                            />
                            <FeatureFlag flag={FeatureFlags.PE_LIBRARY}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        const callback = (string: string) => {
                                            setFieldValue('ctaImage', string);
                                        };
                                        setLibraryCallback(() => callback);
                                        setShowLibrary(true);
                                    }}
                                >
                                    Choose from Library
                                </Button>
                            </FeatureFlag>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

            {/* Media Display Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Media Display Settings
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Configure which types of media are displayed in the
                        gallery.
                    </p>
                </div>

                <FieldComponent>
                    <div className="flex items-center space-x-2">
                        <Field
                            id="galleryMediaFilter.enable"
                            name="galleryMediaFilter.enable"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label
                            htmlFor="galleryMediaFilter.enable"
                            className="text-sm font-medium"
                        >
                            Enable media type filters
                        </Label>
                    </div>
                </FieldComponent>

                {values.galleryMediaFilter.enable && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.original"
                                    name="galleryMediaFilter.original"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="galleryMediaFilter.original"
                                    className="text-sm font-medium"
                                >
                                    Show original images
                                </Label>
                            </div>
                        </FieldComponent>

                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.photos"
                                    name="galleryMediaFilter.photos"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="galleryMediaFilter.photos"
                                    className="text-sm font-medium"
                                >
                                    Show photos
                                </Label>
                            </div>
                        </FieldComponent>

                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.videos"
                                    name="galleryMediaFilter.videos"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="galleryMediaFilter.videos"
                                    className="text-sm font-medium"
                                >
                                    Show videos
                                </Label>
                            </div>
                        </FieldComponent>

                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.edited"
                                    name="galleryMediaFilter.edited"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="galleryMediaFilter.edited"
                                    className="text-sm font-medium"
                                >
                                    Show edited images
                                </Label>
                            </div>
                        </FieldComponent>

                        <FieldComponent>
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.prints"
                                    name="galleryMediaFilter.prints"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <Label
                                    htmlFor="galleryMediaFilter.prints"
                                    className="text-sm font-medium"
                                >
                                    Show prints
                                </Label>
                            </div>
                        </FieldComponent>
                    </div>
                )}
            </div>

            {/* Separator */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

            {/* Tag Filter */}
            <GalleryTagFilterSettings
                value={values.galleryTagFilter}
                onChange={(next) => setFieldValue('galleryTagFilter', next)}
                activationId={values.activationId}
            />

            {/* Separator */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-8"></div>

            {/* Misc Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Misc Settings
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Additional configuration options for the gallery.
                    </p>
                </div>

                <FieldComponent>
                    <Label htmlFor="labelSearchPrompt">
                        Label Search Prompt
                    </Label>
                    <Field
                        as={Input}
                        id="labelSearchPrompt"
                        name="labelSearchPrompt"
                        placeholder="Enter search prompt for labels"
                    />
                </FieldComponent>

                {hasPictimeSettings && (
                    <FieldComponent>
                        <div className="flex items-center space-x-2">
                            <Field
                                id="syncWithPictime"
                                name="syncWithPictime"
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <Label
                                htmlFor="syncWithPictime"
                                className="text-sm font-medium"
                            >
                                Sync with Pic-Time
                            </Label>
                        </div>
                    </FieldComponent>
                )}
            </div>

            {/* Library Modal */}
            {showLibrary && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center z-50"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowLibrary(false);
                        setLibraryCallback(null);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            if (libraryCallback) {
                                libraryCallback(value);
                            }
                            setLibraryCallback(null);
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowLibrary(false);
                            setLibraryCallback(null);
                        }}
                    />
                </div>
            )}
        </div>
    );
}
